import type { FC } from 'react';
import {
  TableCell,
  TableRow,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import { formatMoney } from 'utils/helpers';
import type { RevenueEntry } from '../types';
import useStyles from './RevenueDetailTable.styles';

type RevenueDetailRowProps = {
  readonly entry: RevenueEntry;
};

type ContextItem = { text: string; numeric?: boolean };

const RevenueDetailRow: FC<RevenueDetailRowProps> = ({ entry }) => {
  const classes = useStyles();
  const { t } = useTranslation('revenue');
  const isOverride = entry.available && entry.raw.amount_override !== 0;

  const ctxList: ContextItem[] = [];
  if (entry.available) {
    if (entry.raw.posted === true) ctxList.push({ text: t('table.posted', 'Posted') });
    if (entry.raw.posted === false) ctxList.push({ text: t('table.unposted', 'Unposted') });
    if (isOverride) ctxList.push({ text: t('table.override', 'Override applied') });
    if (entry.raw.months != null) ctxList.push({ text: `${entry.raw.months} ${t('table.months', 'months')}`, numeric: true });
    if (entry.raw.per_month != null) ctxList.push({ text: `${formatMoney(entry.raw.per_month)} / ${t('table.month', 'month')}`, numeric: true });
    if (!entry.hasBasis && entry.basisNote) ctxList.push({ text: t('kpi.basisNote', entry.basisNote) });
  } else {
    ctxList.push({ text: t('table.unavailable', 'Not available for this order') });
  }

  return (
    <TableRow
      key={entry.code}
      hover
      className={`${classes.dataRow} ${entry.available ? '' : classes.unavailableRow}`}
    >
      <TableCell className={`${classes.bodyCell} ${classes.groupCell}`}>
        {t(`groupLabels.${entry.group}`, '')}
      </TableCell>
      <TableCell className={`${classes.bodyCell} ${classes.descriptionCell}`}>
        <Typography variant="body2" className={classes.descriptionLine}>
          {entry.label}
        </Typography>
        {entry.available && entry.raw.description && entry.raw.description !== entry.label && (
          <Typography variant="caption" className={classes.rawDescription}>
            {entry.raw.description}
          </Typography>
        )}
        {entry.framing && (
          <Typography variant="caption" className={classes.framing}>
            {entry.framing}
          </Typography>
        )}
      </TableCell>
      <TableCell className={`${classes.bodyCell} ${classes.amountCell}`} align="right">
        {isOverride && (
          <Typography variant="caption" className={classes.overrideTag}>
            {t('table.overrideShort', 'Override')}
          </Typography>
        )}
        <Typography component="span" className={classes.amountValue}>
          {entry.display}
        </Typography>
      </TableCell>
      <TableCell className={`${classes.bodyCell} ${classes.contextCell}${ctxList.length === 0 ? ` ${classes.contextCellEmpty}` : ''}`}>
        {ctxList.map((item) => (
          <Typography
            variant="caption"
            className={`${classes.contextItem}${item.numeric ? ` ${classes.numericText}` : ''}`}
            key={`${entry.code}-${item.text}`}
          >
            {item.text}
          </Typography>
        ))}
      </TableCell>
    </TableRow>
  );
};

export { RevenueDetailRow };
export default RevenueDetailRow;
